import React, { useState } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Button,
  Box
} from "@mui/material";

import MailboxDetails from "./MailboxDetails";
import CreateMailbox from "./CreateMailbox";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReorderIcon from "@mui/icons-material/Reorder";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import "./voiceMail.css";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";

const VoicemailPage = ({ voicemail = [] }) => {
  const [selectedMailboxPage, setSelectedMailboxPage] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [loading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [mailboxes, setMailboxes] = useState(voicemail);
  const [loadingMessages, setLoadingMessages] = useState(false);

  /* ================= HELPER: CALCULATE AUDIO DURATION ================= */
  const calculateDuration = (url) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration;
        if (isNaN(duration) || duration === Infinity) {
          resolve("Unknown");
        } else {
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      });
      audio.addEventListener('error', () => {
        resolve("Unknown");
      });
      audio.src = url;
    });
  };

  /* ================= FETCH MAILBOX VOICEMAILS ================= */
  const handleMailboxClick = async (mailbox) => {
    setSelectedMailboxPage(mailbox);
    setLoadingMessages(true);

    const mode =
      mailbox.type?.toLowerCase() === "queue" ? "queue" : "agent";

    try {
      const response = await axiosInstance.post(
        endpoints.voiceMailsDisplay.voiceMailAudioList,
        { mode: mode }, // axios automatically sends JSON
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      // Axios already parses JSON
      const parsedBody =
        typeof response.data.body === "string"
          ? JSON.parse(response.data.body)
          : response.data.body;

      console.log("Fetched voicemails:", parsedBody.voicemails);

      const mappedMessagesPromises = parsedBody.voicemails.map(
        async (item) => {
          const duration = await calculateDuration(item.presignedUrl);

          return {
            id: item.contactId,
            created: item.voicemailTimestamp,
            from: item.customerPhoneNumber,
            audioUrl: item.recordingKey,
            presignedUrl: item.presignedUrl,
            duration: duration
          };
        }
      );

      const mappedMessages = await Promise.all(mappedMessagesPromises);
      setMessages(mappedMessages);

    } catch (error) {
      console.error("Error fetching voicemails:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  /* ================= REDIRECT TO MAILBOX DETAILS ================= */
  if (selectedMailboxPage) {
    return (
      <MailboxDetails
        mailbox={selectedMailboxPage}
        messages={messages}
        loading={loadingMessages}
        onBack={() => {
          setSelectedMailboxPage(null);
          setMessages([]);
        }}
      />
    );
  }

  /* ================= DOWNLOAD CSV ================= */
  const handleDownloadCSV = () => {
    if (!voicemail.length) return;

    const headers = ["Name", "Status", "Type", "Created"];

    const rows = voicemail.map((mailbox) => [
      mailbox.name,
      "Enabled",
      mailbox.type,
      mailbox.created
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) => row.join(","))
        .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = "voicemail_sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedMailbox(null);
  };
  const handleAddMailbox = (newMailbox) => {
    setMailboxes((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        ...newMailbox
      }
    ]);
  };

  const handleDeleteMailbox = async (mailbox, e) => {
    e.stopPropagation(); // Prevent row click

    try {
      const payload = {
        Name: mailbox.name,
        Action: "Delete"
      };

      const response = await axiosInstance.post(
        endpoints.voiceMailsDisplay.voiceMailList,
        payload,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      console.log("Delete API Response:", response.data);

      // ✅ Remove from UI only after successful API call
      setMailboxes((prev) =>
        prev.filter((item) => item.id !== mailbox.id)
      );

    } catch (error) {
      console.error("Error deleting mailbox:", error);
    }
  };

  return (
    <Paper
      sx={{
        p: 0,
        width: "100%",
        textAlign: "left"
      }}
    >
      {/* ================= HEADER ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        py={2}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa"
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            instance-iq
          </Typography>
          <Typography variant="body2" color="text.secondary">
            6b8ccfef-729e-4aaa-9c99-14d8e0192e30
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2
            }}
          >
            Download CSV
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateModal(true)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              backgroundColor: "#4b5563",
              "&:hover": { backgroundColor: "#374151" }
            }}
          >
            Create a Mailbox
          </Button>
        </Box>
      </Box>

      {/* ================= TABLE ================= */}
      <Box p={3}>
        <Table className="voicemail-table">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {mailboxes.map((mailbox) => (
              <TableRow
                key={mailbox.id}
                hover
                onClick={() => handleMailboxClick(mailbox)}
                style={{ cursor: "pointer" }}
              >
                {/* NAME */}
                <TableCell>{mailbox.name}</TableCell>

                {/* STATUS */}
                <TableCell>
                  <span className="status-pill status-enabled">
                    <CheckCircleIcon className="pill-icon success-icon" />
                    Enabled
                  </span>
                </TableCell>

                {/* TYPE */}
                <TableCell>
                  <span className="status-pill type-pill">
                    {mailbox.type === "Queue" ? (
                      <>
                        <ReorderIcon className="pill-icon type-icon" />
                        Queue
                      </>
                    ) : (
                      <>
                        <PersonIcon className="pill-icon type-icon" />
                        Agent
                      </>
                    )}
                  </span>
                </TableCell>

                {/* CREATED */}
                <TableCell>{mailbox.created}</TableCell>
                <TableCell>
                  <IconButton
                    color="error"
                    onClick={(e) => handleDeleteMailbox(mailbox, e)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* ================= CREATE MAILBOX MODAL ================= */}
      <CreateMailbox
        open={openCreateModal}
        handleClose={() => setOpenCreateModal(false)}
        onMailboxCreated={handleAddMailbox}
        existingMailboxes={mailboxes}
      />

      {/* ================= CONFIGURE MODAL ================= */}
      <Dialog open={openModal} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configure Voicemail - {selectedMailbox?.name}
        </DialogTitle>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained">
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default VoicemailPage;